/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import type {
  AppearancePrefs,
  NotificationPrefs,
  Preferences,
  PreferencesBundle,
} from "@/hooks/ui";
import { useUserPreferences as useUnifiedUserPreferences } from "@/hooks/ui";
import { useThemeModeContext } from "@/components/providers/theme-mode-provider";
import {
  applyDensityToDOM,
  getDensityPreference,
  setDensityPreference,
} from "@/lib/utils/design/theme-cookies";
import React, { useCallback, useEffect, useMemo } from "react";

function adaptPreferencesBundle(
  bundle: Partial<PreferencesBundle>,
): Preferences {
  const theme = bundle.ui?.theme;
  let darkMode = false;
  if (theme === "dark") {
    darkMode = true;
  } else if (theme === "system" && typeof window !== "undefined") {
    darkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  return {
    appearance: {
      darkMode,
      compactMode: bundle.ui?.density === "compact",
      showAnimations: bundle.ui?.animationsEnabled ?? true,
    },
    notifications: {
      emailNotifications: bundle.notifications?.email?.enabled ?? true,
      pushNotifications: bundle.notifications?.push?.enabled ?? false,
      projectUpdates: bundle.notifications?.inApp?.enabled ?? true,
      estimateAlerts: bundle.notifications?.inApp?.enabled ?? true,
      proposalNotifications: bundle.notifications?.inApp?.enabled ?? true,
    },
  };
}

type UserPreferencesValue = {
  prefs: Preferences;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  saveAppearance: (appearance: AppearancePrefs) => Promise<void>;
  saveNotifications: (notifications: NotificationPrefs) => Promise<void>;
  saveAll: (payload: Partial<Preferences>) => Promise<void>;
};

const UserPreferencesContext = React.createContext<UserPreferencesValue | null>(
  null,
);

export function UserPreferencesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const newPreferences = useUnifiedUserPreferences();
  const { setMode } = useThemeModeContext();

  const adaptedPrefs = useMemo(
    () => adaptPreferencesBundle(newPreferences.preferences),
    [newPreferences.preferences],
  );

  const saveAppearance = useCallback(
    async (appearance: AppearancePrefs) => {
      setMode(appearance.darkMode ? "dark" : "light");
      await newPreferences.updatePreferences("ui", {
        theme: appearance.darkMode ? "dark" : "light",
        density: appearance.compactMode ? "compact" : "comfortable",
        animationsEnabled: appearance.showAnimations,
      });
    },
    [newPreferences, setMode],
  );

  const saveNotifications = useCallback(
    async (notifications: NotificationPrefs) => {
      await newPreferences.updatePreferences("notifications", {
        email: {
          enabled: notifications.emailNotifications,
          frequency: "immediate",
          types: ["project", "estimate", "proposal"],
        },
        push: {
          enabled: notifications.pushNotifications,
          types: ["project", "estimate", "proposal"],
        },
        inApp: {
          enabled: notifications.projectUpdates,
          types: notifications.projectUpdates
            ? ["project", "estimate", "proposal"]
            : [],
        },
      });
    },
    [newPreferences],
  );

  const saveAll = useCallback(
    async (payload: Partial<Preferences>) => {
      if (payload.appearance) {
        await saveAppearance(payload.appearance);
      }
      if (payload.notifications) {
        await saveNotifications(payload.notifications);
      }
    },
    [saveAppearance, saveNotifications],
  );

  const refresh = useCallback(async () => {
    await newPreferences.refreshPreferences();
  }, [newPreferences]);

  const value = useMemo<UserPreferencesValue>(
    () => ({
      prefs: adaptedPrefs,
      loading: newPreferences.isLoading,
      error: newPreferences.error,
      refresh,
      saveAppearance,
      saveNotifications,
      saveAll,
    }),
    [
      adaptedPrefs,
      newPreferences.isLoading,
      newPreferences.error,
      refresh,
      saveAppearance,
      saveNotifications,
      saveAll,
    ],
  );

  // Apply UI density from API preferences when they load
  const densityValue = (
    newPreferences.preferences as Record<string, unknown> | undefined
  )?.ui as Record<string, unknown> | undefined;
  const apiDensity = densityValue?.density as string | undefined;

  useEffect(() => {
    if (!apiDensity) return;
    const stored = getDensityPreference();
    if (stored === apiDensity) return;
    setDensityPreference(apiDensity);
    applyDensityToDOM(apiDensity);
  }, [apiDensity]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferencesContext(): UserPreferencesValue {
  const context = React.useContext(UserPreferencesContext);
  if (!context) {
    throw new Error(
      "useUserPreferencesContext must be used within UserPreferencesProvider",
    );
  }
  return context;
}

// Backward-compat export used elsewhere
export const useUserPreferences = useUserPreferencesContext;
